import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Apollo, gql, APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { map, BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { User } from './user.service';
import { environment } from 'src/environments/environment';

export interface AuthError {
  message: string
}

export interface AuthAccessToken {
  token: string
  expires_at: number
}

export interface AuthLoginSuccess {
  token: AuthAccessToken
  user: User
}

export interface AuthLogoutSuccess {
  tokens: number
  user: User
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<any>(false);
  public redirectUrl: string = environment.BASE_HREF + '/';

  constructor(
    private apollo: Apollo,
    private router: Router,
    private location: Location
  ) {
		this.isAuthenticated.next( !!localStorage.getItem( 'token' ) );
  }

  // Login
  login( email: string, password: string ): Observable<any> {
    const LOGIN =  gql`
      mutation Login {
        authLogin(input: {email: "${email}", password: "${password}"}) {
          ... on AuthLoginSuccess {
            __typename
            token {
              expires_at
              token
            }
            user {
              id
              first_name
              last_name
              title
              role
              email
              mobile
              phone
              is_active
              created_at
              updated_at
              manager {
                id
              }
              organizations {
                id
              }
              favoriteProjects {
                id
              }
            }
          }
          ... on AuthError {
            __typename
            message
          }
        }
      }
    `;

    return this.apollo.mutate({
      mutation: LOGIN
    });
  }


  // Admin-only: emulate another user. Server returns a fresh Sanctum token
  // tagged "login-as:{adminId}" for that user. The caller is responsible for
  // stashing the admin's existing token/user under `originalToken`/`originalUser`
  // before swapping in the impersonation session.
  loginAs( userId: string | number ): Observable<any> {
    const LOGIN_AS = gql`
      mutation LoginAs($user_id: ID!) {
        authLoginAs(user_id: $user_id) {
          ... on AuthLoginSuccess {
            __typename
            token {
              expires_at
              token
            }
            user {
              id
              first_name
              last_name
              title
              role
              email
              mobile
              phone
              is_active
              created_at
              updated_at
              manager {
                id
              }
              organizations {
                id
              }
              favoriteProjects {
                id
              }
            }
          }
          ... on AuthError {
            __typename
            message
          }
        }
      }
    `;

    return this.apollo.mutate({
      mutation: LOGIN_AS,
      variables: { user_id: userId }
    });
  }


  // Are we currently in an admin-impersonation session?
  isEmulating(): boolean {
    return !!localStorage.getItem( 'originalToken' );
  }


  // Restore the admin's original session from the stash and return to the dashboard.
  // Does NOT call authLogout on the impersonation token — that token remains in
  // personal_access_tokens for audit/cleanup. (Use authLogoutAll from your own
  // account to clear any lingering impersonation tokens you've issued.)
  revertLogin() {
    const originalToken = localStorage.getItem( 'originalToken' );
    const originalUser  = localStorage.getItem( 'originalUser' );
    if ( !originalToken || !originalUser ) {
      return;
    }
    localStorage.setItem( 'token', originalToken );
    localStorage.setItem( 'currentUser', originalUser );
    localStorage.removeItem( 'originalToken' );
    localStorage.removeItem( 'originalUser' );
    // Hard reload so Apollo cache and all component state are rebuilt under the admin token.
    window.location.href = environment.BASE_HREF + '/';
  }


  // Logout
  logout(): Observable<any> {
    const LOGOUT = gql`
      mutation Logout {
        authLogout {
          ... on AuthLogoutSuccess {
            __typename
            tokens
            user {
              id
            }
          }
          ... on AuthError {
            __typename
            message
          }
        }
      }
    `;

    return this.apollo.mutate({
      mutation: LOGOUT
    });
  }


  // Logout All
  logoutAll() {
    const LOGOUT = gql`
      mutation Logout {
        authLogoutAll {
          ... on AuthLogoutSuccess {
            __typename
            tokens
            user {
              id
            }
          }
          ... on AuthError {
            __typename
            message
          }
        }
      }
    `;

    this.apollo.mutate({
      mutation: LOGOUT
    }).subscribe({
      next: (res: any) => {
        localStorage.clear();
        this.isAuthenticated.next( false );
        // this.apollo.client.resetStore();
        this.location.replaceState('/login');
        this.router.navigate(['/login']);
        return false;
      },
      error: (err: any) => {
        console.error( err );
      }
    });
  }


  // Current Logged In User
  getCurrentUser() {
    const GET_USER = gql`
      query currentUser {
        authUser {
          id
          first_name
          last_name
          title
          role
          email
          mobile
          phone
          is_active
          created_at
          updated_at
          manager {
            id
            first_name
            last_name
            email
            phone
            mobile
            role
            title
            notes
            is_active
          }
          organizations {
            id
          }
          favoriteProjects {
            id
          }
        }
      }
    `;

    return this.apollo.watchQuery({
      query: GET_USER
    }).valueChanges.pipe( map( ( result: any ) => result.data ) );
  }

}
